import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as TimelineFlightInformationService from "_services/timeline-flight-information-service";

/**
 * Get passenger list for a specific flight - bypasses zone and date range filters
 * This thunk fetches bookings directly from the server for a specific flight,
 * useful when viewing a flight that may not be in the current timeline cache
 * (e.g., flight from a different zone or outside the current date range)
 */
export const getFlightPassengerList = createAsyncThunk(
    "timelineFlightInformation/getFlightPassengerList",
    async ({ iata, flightNumber, departureDate, type }) => {
        return TimelineFlightInformationService.getFlightPassengerList({
            iata,
            flightNumber,
            departureDate,
            type,
        });
    }
);

const timelineFlightInformationSlice = createSlice({
    name: 'timelineFlightInformation',
    initialState: {
        // Flight passenger list data
        passengerList: [],
        
        // Employee list for the flight (full employee documents)
        employeeList: [],
        
        // Request metadata
        currentFlightKey: null, // Track which flight the data belongs to
        
        // Status tracking
        status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
        error: null,
    },
    reducers: {
        /**
         * Reset the flight information state
         * Call this when closing the flight view modal or switching flights
         */
        reset: (state) => {
            state.passengerList = [];
            state.employeeList = [];
            state.currentFlightKey = null;
            state.status = 'idle';
            state.error = null;
        },
        
        /**
         * Clear just the passenger list (keep other state)
         */
        clearPassengerList: (state) => {
            state.passengerList = [];
            state.employeeList = [];
            state.currentFlightKey = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getFlightPassengerList.pending, (state, action) => {
                state.status = "loading";
                state.error = null;
                // Store the flight key from the request for tracking
                const { flightNumber, departureDate } = action.meta.arg;
                state.currentFlightKey = `${flightNumber}_${departureDate}`;
            })
            .addCase(getFlightPassengerList.fulfilled, (state, action) => {
                state.status = "succeeded";
                // API returns { bookings: [], employeeList: [] }
                const payload = action.payload || {};
                state.passengerList = payload.bookings || [];
                state.employeeList = payload.employeeList || [];
            })
            .addCase(getFlightPassengerList.rejected, (state, action) => {
                state.status = "failed";
                state.passengerList = [];
                state.employeeList = [];
                state.error = action.error.message;
            });
    }
});

export default timelineFlightInformationSlice.reducer;

// Export actions
export const { reset, clearPassengerList } = timelineFlightInformationSlice.actions;

// Selectors
export const selectFlightPassengerList = state => state.timelineFlightInformation.passengerList;
export const selectFlightEmployeeList = state => state.timelineFlightInformation.employeeList;
export const selectFlightPassengerListStatus = state => state.timelineFlightInformation.status;
export const selectFlightPassengerListError = state => state.timelineFlightInformation.error;
export const selectCurrentFlightKey = state => state.timelineFlightInformation.currentFlightKey;

/**
 * Select if the flight passenger list is currently loading
 */
export const selectIsLoading = state => state.timelineFlightInformation.status === 'loading';

/**
 * Select if the flight passenger list has loaded successfully
 */
export const selectHasLoaded = state => state.timelineFlightInformation.status === 'succeeded';
